import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FolderTree, Plus, Pencil, Trash2, Loader2, Globe, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

export default function Categories() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/categories");
      setTree(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDialog = (mode, payload = {}) => {
    setName(mode === "rename" ? payload.target.name : "");
    setDialog({ mode, ...payload });
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Naam likhna zaroori hai");
    setSaving(true);
    try {
      if (dialog.mode === "add-cat") await api.post("/categories", { name: name.trim(), parent_id: "" });
      else if (dialog.mode === "add-sub") await api.post("/categories", { name: name.trim(), parent_id: dialog.parent.id });
      else await api.put(`/categories/${dialog.target.id}`, { name: name.trim() });
      toast.success("Saved — website par live ho gaya");
      setDialog(null);
      setName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Is category ki medicines delete nahi hongi, sirf website menu se hat jayegi.`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const dialogTitle = dialog?.mode === "add-cat"
    ? "Nayi Category"
    : dialog?.mode === "add-sub"
      ? `Sub-Category — ${dialog?.parent?.name}`
      : `Rename — ${dialog?.target?.name}`;

  return (
    <div className="space-y-4" data-testid="categories-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Categories</h1>
          <p className="text-sm text-slate-500">Website ke Shop menu ki categories aur sub-categories yahan manage karein.</p>
        </div>
        <Button data-testid="add-category-btn" onClick={() => openDialog("add-cat")} className="bg-emerald-900 hover:bg-emerald-800">
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      <div className="flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg px-3 py-2.5" data-testid="categories-live-note">
        <Globe className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Yahan jo bhi category/sub-category add ya rename karenge, wo <b>website ke Shop dropdown aur sidebar par turant live</b> dikhegi. Medicines add/edit karte waqt yahi categories dropdown mein aayengi.</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tree.map((c) => (
            <Card key={c.id || c.name} data-testid={`category-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`} className="p-4 border-slate-200">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FolderTree className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span className="font-bold text-slate-900 truncate">{c.name}</span>
                </div>
                <div className="flex shrink-0">
                  <Button size="icon" variant="ghost" title="Add sub-category" className="h-7 w-7" onClick={() => openDialog("add-sub", { parent: c })} data-testid={`add-sub-${c.id}`}><Plus className="w-3.5 h-3.5 text-emerald-700" /></Button>
                  <Button size="icon" variant="ghost" title="Rename" className="h-7 w-7" onClick={() => openDialog("rename", { target: c })} data-testid={`rename-cat-${c.id}`}><Pencil className="w-3.5 h-3.5 text-sky-700" /></Button>
                  <Button size="icon" variant="ghost" title="Delete" className="h-7 w-7" onClick={() => remove(c)} data-testid={`delete-cat-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                </div>
              </div>
              <div className="mt-2 space-y-1">
                {(c.children || []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5" data-testid={`subcategory-row-${s.id}`}>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <CornerDownRight className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="text-xs font-medium text-slate-700 truncate">{s.name}</span>
                    </div>
                    <div className="flex shrink-0">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openDialog("rename", { target: s })} data-testid={`rename-sub-${s.id}`}><Pencil className="w-3 h-3 text-sky-700" /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => remove(s)} data-testid={`delete-sub-${s.id}`}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                    </div>
                  </div>
                ))}
                {(c.children || []).length === 0 && (
                  <div className="text-[11px] text-slate-400 italic px-1">Koi sub-category nahi — + se add karein</div>
                )}
              </div>
            </Card>
          ))}
          {tree.length === 0 && (
            <div className="col-span-full text-center py-12 text-sm text-slate-400">Koi category nahi hai. "Add Category" se shuru karein.</div>
          )}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm bg-white" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-display">{dialogTitle}</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs">Naam *</Label>
            <Input data-testid="category-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-10" placeholder="e.g. Baby Care" autoFocus onKeyDown={(e) => e.key === "Enter" && submit()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !name.trim()} data-testid="category-save-btn" className="bg-emerald-900 hover:bg-emerald-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
